/**
 * Componente de Dashboard Completo com Analytics
 * Seguindo Clean Architecture and Composition Pattern
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useCamerasContext } from '../../contexts/CamerasContext';
import { useRealtimeMonitoring } from '../hooks/useRealtimeMonitoring';
import { useAuth } from '../../contexts/AuthContext';
import { GenderBreakdownChart } from './GenderBreakdownChart';
import { AgeDistributionChart } from './AgeDistributionChart';
// 🎭 Emotion Analytics — Feature Premium (só renderiza com flag habilitada)
import { EmotionBreakdownChart } from './EmotionBreakdownChart';
import { EmotionByHourChart } from './EmotionByHourChart';
import { EmotionDailySummaryCard } from './EmotionDailySummaryCard';
import { TimelineChart } from './TimelineChart';
import { TemporalComparisonChart } from './TemporalComparisonChart';
import { ComparativeTable, DailyPoint } from './ComparativeTable';
import { computeComparativeRange } from '../utils/computeComparativeRange';
import { EntranceExitDistribution } from './EntranceExitDistribution';
import { WeeklyFlowChart, WeeklyFlowData } from './WeeklyFlowChart';
import { MonthlyComparisonChart, MonthlyData } from './MonthlyComparisonChart';
import { LoadingSpinner } from './LoadingSpinner';
import { PeopleInsidePanel } from './PeopleInsidePanel';
import { ConversionPanel } from './ConversionPanel';
import { FilterPanel, PeriodType } from './FilterPanel';
import { GenderStats } from '../../domain/entities/Analytics';
import { ReportsService, FilteredStats } from '../../data/services/ReportsService';
import { HistoricalImportService } from '../../data/services/HistoricalImportService';
import { DayObservationService, DayObservation } from '../../data/services/DayObservationService';
import { useToast } from '../../components/common/Toast';
import { RefreshCw } from 'lucide-react';
import { APIConfig } from '../../data/config/APIConfig';

/** Retorna cor e rótulo do status da câmera com base na última detecção. */
const getCameraStatus = (lastDetection?: string): { dot: string; label: string } => {
  if (!lastDetection) return { dot: 'bg-gray-400', label: 'Sem dados' };
  const minsAgo = (Date.now() - new Date(lastDetection).getTime()) / 60000;
  if (minsAgo < 5)  return { dot: 'bg-green-400', label: 'Ativo' };
  if (minsAgo < 60) return { dot: 'bg-yellow-400', label: 'Ocioso' };
  return { dot: 'bg-red-400', label: 'Offline' };
};

/**
 * Dashboard completo com todos os gráficos e estatísticas
 */
export const CompleteDashboard: React.FC = () => {
  const { activeOrganizationId, user } = useAuth();
  // All analytics state belongs to one organization. A switch must not expose
  // previous totals or allow its pending requests to update the new dashboard.
  return <CompleteDashboardContent key={activeOrganizationId || user?.organization_id || 'no-organization'} />;
};

const CompleteDashboardContent: React.FC = () => {
  const {
    systemStats,
    demographics,
    emotions,
    hourlyStats,
    loading,
    refresh,
    fetchEmotions,
  } = useAnalytics(false);

  const { cameras } = useCamerasContext();
  // Câmeras de entrada apenas — câmeras de zona (is_entrance=false) ficam só no Heatmap
  const entranceCameras = cameras.filter(c => c.is_entrance !== false);
  const { organizationName, activeOrganizationId, user } = useAuth();
  const effectiveOrgId = activeOrganizationId || user?.organization_id || '';
  const { showToast, ToastContainer } = useToast();
  
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [filteredStats, setFilteredStats] = useState<FilteredStats | null>(null);
  const [filteredPrevYear, setFilteredPrevYear] = useState<{ date: string; people_count: number }[] | null>(null);
  // Dados do período comparativo (mesma duração imediatamente anterior
  // ou ano-a-ano se range >= 365 dias). Usado pela ComparativeTable.
  const [comparativeDailyCurrent, setComparativeDailyCurrent] = useState<DailyPoint[]>([]);
  const [comparativeDailyPrevious, setComparativeDailyPrevious] = useState<DailyPoint[]>([]);
  const [comparativeLabel, setComparativeLabel] = useState<string>('');
  const [comparativeIsYoY, setComparativeIsYoY] = useState<boolean>(false);
  const [comparativeGender, setComparativeGender] = useState<GenderStats | null>(null);
  // Alterna entre visualização gráfica e tabela na seção comparativa
  const [temporalView, setTemporalView] = useState<'chart' | 'table'>('chart');
  const [isFiltered, setIsFiltered] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // ⚠️ Flag para evitar múltiplas chamadas simultâneas
  const filterRequestId = useRef(0);
  const [currentFilters, setCurrentFilters] = useState<{
    period: PeriodType;
    startDate?: string;
    endDate?: string;
    cameraId?: string;
    cameraIds?: string[];
  }>({ period: 'today' });
  
  // 📷 Ref para filtro de câmera — sempre atualizado, evita stale closure no loadWeeklyData
  const cameraFilterRef = useRef<{ cameraId?: string; cameraIds?: string[] }>({});
  useEffect(() => {
    cameraFilterRef.current = {
      cameraId: currentFilters.cameraId,
      cameraIds: currentFilters.cameraIds,
    };
  }, [currentFilters.cameraId, currentFilters.cameraIds]);
  
  // 📊 Estados para novos gráficos
  const [weeklyData, setWeeklyData] = useState<WeeklyFlowData[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [weeklyObservations, setWeeklyObservations] = useState<DayObservation[]>([]);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // 🌎 Timezone da organização (carregado dinamicamente)
  const [orgTimezone, setOrgTimezone] = useState<string>('America/Manaus');
  useEffect(() => {
    if (!effectiveOrgId) return;
    const api = APIConfig.getInstance();
    api.get(`/api/v1/organizations/${effectiveOrgId}/status`)
      .then(res => {
        if (res.data?.timezone) {
          setOrgTimezone(res.data.timezone);
        }
      })
      .catch(() => {}); // silencia erro, mantém fallback
  }, [effectiveOrgId]);
  
  // 🔴 Monitoramento WebSocket para auto-refresh (memoizado)
  const { isConnected, detections } = useRealtimeMonitoring({
    maxDetections: 10,
  });

  // 🎯 Carregar dados filtrados automaticamente na inicialização
  useEffect(() => {
    // Carregar com filtro "today" na primeira carga
    handleFilterChange({ period: 'today' });
    return () => { filterRequestId.current += 1; };
  }, []); // Executar apenas uma vez na montagem
  
  // 🔄 Auto-refresh a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(async () => {
      // ⏸️ PAUSAR auto-refresh se filtragem estiver em andamento
      if (filterLoading) {
        console.warn('⏸️ Auto-refresh pausado: filtragem em andamento');
        return;
      }

      // ⚠️ Evitar múltiplas requisições simultâneas
      if (isRefreshing) {
        console.warn('⏭️ Auto-refresh pulado: requisição anterior ainda em andamento');
        return;
      }

      setIsRefreshing(true);
      try {
        await handleRefresh();
      } catch (err) {
        console.error('❌ Erro no auto-refresh:', err);
        // ⚠️ NÃO bloqueia UI - mantém dados antigos
      } finally {
        setIsRefreshing(false);
      }
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [isFiltered, currentFilters, isRefreshing, filterLoading]);
  
  // 🚀 Refresh imediato quando receber nova detecção via WebSocket
  const previousDetectionsCount = useRef(0);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Só atualiza se realmente houver uma NOVA detecção
    if (detections.length > 0 && detections.length > previousDetectionsCount.current) {
      // Limpar timeout anterior se existir
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Aguardar 2s antes de atualizar (debounce)
      refreshTimeoutRef.current = setTimeout(() => {
        handleRefresh();
      }, 2000);
    }
    previousDetectionsCount.current = detections.length;
    
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [detections.length]);

  // 🎭 Converte o filtro do dashboard (período/câmeras) para os parâmetros do
  // endpoint /stats/emotions — mantém as cards de emoção no MESMO período e
  // nas MESMAS câmeras das estatísticas de contagem (paridade com /filtered).
  const buildEmotionParams = (f: {
    period: PeriodType;
    startDate?: string;
    endDate?: string;
    cameraId?: string;
    cameraIds?: string[];
  }) => {
    const p: {
      period?: PeriodType;
      start_date?: string;
      end_date?: string;
      camera_id?: string;
      camera_ids?: string;
    } = {};
    if (f.period === 'custom' && f.startDate && f.endDate) {
      p.start_date = f.startDate;
      p.end_date = f.endDate;
    } else {
      p.period = f.period;
    }
    if (f.cameraId) {
      p.camera_id = f.cameraId;
    } else if (f.cameraIds && f.cameraIds.length > 0) {
      p.camera_ids = f.cameraIds.join(',');
    }
    return p;
  };

  const handleFilterChange = async (filters: {
    period: PeriodType;
    startDate?: string;
    endDate?: string;
    cameraId?: string;
    cameraIds?: string[];
  }) => {
    const requestId = ++filterRequestId.current;
    setCurrentFilters(filters);
    setFilterLoading(true);
    try {
      // Calcular range do ano anterior (mesmo período, -1 ano)
      const computePrevYearRange = () => {
        const now = new Date();
        const fmt = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        let start: Date, end: Date;
        if (filters.period === 'custom' && filters.startDate && filters.endDate) {
          start = new Date(filters.startDate + 'T12:00:00');
          end = new Date(filters.endDate + 'T12:00:00');
        } else if (filters.period === 'today') {
          start = new Date(now); end = new Date(now);
        } else if (filters.period === 'week') {
          end = new Date(now); start = new Date(now); start.setDate(start.getDate() - 7);
        } else if (filters.period === 'month') {
          end = new Date(now); start = new Date(now); start.setDate(start.getDate() - 30);
        } else return null;
        start.setFullYear(start.getFullYear() - 1);
        end.setFullYear(end.getFullYear() - 1);
        return { start: fmt(start), end: fmt(end) };
      };

      const prevRange = computePrevYearRange();

      // 🚀 Cache do ano anterior (dados históricos não mudam — TTL de 24h)
      const getPrevYearCached = async (start: string, end: string) => {
        const cacheKey = `prev_year_hist_${start}_${end}_${effectiveOrgId}`;
        try {
          const raw = localStorage.getItem(cacheKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            const hoursDiff = (Date.now() - new Date(parsed.timestamp).getTime()) / 3_600_000;
            if (hoursDiff < 24) return parsed.data;
          }
        } catch { /* cache corrompido, refazer */ }
        const result = await HistoricalImportService.getWeeklyHistorical(start, end);
        if (result.has_data) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify({ data: result, timestamp: new Date().toISOString() }));
          } catch { /* quota excedida, ignora */ }
        }
        return result;
      };

      // Publish the selected period as soon as it arrives. Historic comparison
      // must not hold conversion cards hostage; stale requests must not win.
      const statsRequest = ReportsService.getFilteredStats(filters).then(data => {
        if (requestId === filterRequestId.current) {
          setFilteredStats(data);
          setIsFiltered(true);
          setLastUpdate(new Date());
        }
        return data;
      });
      const [statsResult, prevYearResult] = await Promise.allSettled([
        statsRequest,
        prevRange
          ? getPrevYearCached(prevRange.start, prevRange.end)
          : Promise.resolve(null),
      ]);

      if (requestId !== filterRequestId.current) return;

      // 🎭 Análise de Emoções sincronizada com o filtro ativo (período/câmeras).
      // Sem isso, o bloco de emoções só olhava para o dia atual (ignorava o
      // filtro de 7/30 dias e a câmera selecionada).
      // Fire-and-forget: NÃO segura o filtro esperando a agregação de emoções.
      // Com flag OFF o backend responde {enabled:false} → o bloco nem renderiza
      // e o dashboard continua idêntico ao comportamento anterior.
      fetchEmotions(buildEmotionParams(filters)).catch(() => {});

      if (prevYearResult.status === 'fulfilled' && prevYearResult.value?.has_data) {
        setFilteredPrevYear(prevYearResult.value.days);
      } else {
        setFilteredPrevYear(null);
      }

      // 📊 Tabela Comparativa — buscar daily_flow do período comparativo equivalente
      try {
        const compRange = computeComparativeRange(
          filters.period,
          filters.startDate,
          filters.endDate,
        );
        if (compRange && statsResult.status === 'fulfilled') {
          setComparativeLabel(compRange.label);
          // Daily flow do período atual (já vem no filteredStats)
          const curDaily: DailyPoint[] = (statsResult.value.daily_flow ?? []).map(d => ({
            date: d.date,
            people: d.people,
          }));
          setComparativeDailyCurrent(curDaily);

          // Daily flow do período comparativo: nova chamada ao /filtered
          const prevStats = await ReportsService.getFilteredStats({
            period: 'custom',
            startDate: compRange.previous.start,
            endDate: compRange.previous.end,
            cameraId: filters.cameraId,
            cameraIds: filters.cameraIds,
          }).catch(() => null);
          if (requestId !== filterRequestId.current) return;
          const prevDaily: DailyPoint[] = (prevStats?.daily_flow ?? []).map(d => ({
            date: d.date,
            people: d.people,
          }));
          setComparativeDailyPrevious(prevDaily);
          setComparativeIsYoY(compRange.isYearOverYear);
          // Comparativo de gênero: só faz sentido quando YoY E houver dados reais
          const genderPrev = compRange.isYearOverYear && prevStats?.demographics?.gender;
          const hasGenderData = genderPrev && (genderPrev.male > 0 || genderPrev.female > 0);
          setComparativeGender(hasGenderData ? genderPrev : null);
        } else if (!compRange) {
          // Período sem range comparativo definido → limpar explicitamente
          setComparativeDailyCurrent([]);
          setComparativeDailyPrevious([]);
          setComparativeLabel('');
          setComparativeIsYoY(false);
          setComparativeGender(null);
        }
        // Se statsResult falhou (timeout/erro), mantém dados anteriores visíveis
      } catch (err) {
        console.error('Erro ao calcular tabela comparativa:', err);
        // Não limpa os dados — mantém o que estava exibido antes do erro
      }
    } catch (err) {
      console.error('Erro ao aplicar filtros:', err);
    } finally {
      if (requestId === filterRequestId.current) setFilterLoading(false);
    }
  };

  // 📊 Carregar dados semanais (últimos 7 dias) - COM CACHE
  const loadWeeklyData = useCallback(async () => {
    setLoadingWeekly(true);
    try {
      // 📷 Ler filtro de câmera do ref (sempre atualizado, evita stale closure)
      const camId = cameraFilterRef.current.cameraId;
      const camIds = cameraFilterRef.current.cameraIds;

      // 🌎 Usar timezone da organização para calcular "hoje"
      const now = new Date();
      const orgNow = new Date(now.toLocaleString('en-US', { timeZone: orgTimezone }));
      
      const today = new Date(orgNow.getFullYear(), orgNow.getMonth(), orgNow.getDate());
      
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const camFilterPart = camIds?.join(',') || camId || 'all';
      const weeklyCacheKey = `weekly_data_v3_${todayStr}_${effectiveOrgId}_${camFilterPart}`;
      
      // 🧹 Limpar caches antigos (datas diferentes de hoje ou org diferente)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('weekly_data_') && key !== weeklyCacheKey) {
          localStorage.removeItem(key);
        }
      });
      
      // 🚀 Tentar carregar do cache primeiro
      const cached = localStorage.getItem(weeklyCacheKey);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const cacheTime = new Date(cachedData.timestamp);
          const nowTime = new Date();
          const ageMs = nowTime.getTime() - cacheTime.getTime();
          
          // Includes the current day: match the API's 60-second freshness budget.
          if (ageMs >= 0 && ageMs < 60_000) {
            setWeeklyData(cachedData.data);
            // Restaurar observações do cache imediatamente
            if (cachedData.observations) {
              setWeeklyObservations(cachedData.observations);
            }
            setLoadingWeekly(false);
            // 🔄 Refrescar observações em background (podem mudar ao longo do dia)
            const weekAgoRefresh = new Date(today);
            weekAgoRefresh.setDate(weekAgoRefresh.getDate() - 6);
            const weekAgoRefreshStr = `${weekAgoRefresh.getFullYear()}-${String(weekAgoRefresh.getMonth() + 1).padStart(2, '0')}-${String(weekAgoRefresh.getDate()).padStart(2, '0')}`;
            DayObservationService.list(weekAgoRefreshStr, todayStr)
              .then(freshObs => {
                setWeeklyObservations(freshObs);
                // Atualizar cache com observações frescas
                const updatedCache = { ...cachedData, observations: freshObs };
                localStorage.setItem(weeklyCacheKey, JSON.stringify(updatedCache));
              })
              .catch(() => { /* silencioso */ });
            return;
          }
        } catch (e) {
          // Cache inválido, recarregar
        }
      }

      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6); // Últimos 7 dias incluindo hoje
      const weekAgoStr = `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, '0')}-${String(weekAgo.getDate()).padStart(2, '0')}`;

      // Buscar dados do período inteiro em 1 chamada (em vez de 7 chamadas individuais)
      // O backend retorna daily_flow com a quebra por dia — evita 503 por excesso de req.
      const singleResult = await ReportsService.getFilteredStats({
        period: 'custom',
        startDate: weekAgoStr,
        endDate: todayStr,
        cameraId: camId || undefined,
        cameraIds: camIds || undefined,
      });

      const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      const dayNamesShort = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

      // Construir mapa dia → {people, detections} a partir do daily_flow
      const dailyMap: Record<string, { people: number; detections: number }> = {};
      for (const d of singleResult.daily_flow ?? []) {
        dailyMap[d.date] = { people: d.people ?? 0, detections: d.detections ?? 0 };
      }

      const availability = new Map((singleResult.daily_availability ?? []).map(d => [d.date, d]));
      const weekly: WeeklyFlowData[] = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(weekAgo);
        date.setDate(date.getDate() + i);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const dayWithDate = `${dayNamesShort[date.getDay()]} ${day}/${month}`;
        const dayData = dailyMap[dateStr];
        const status = availability.get(dateStr)?.status ?? (dayData ? (dateStr === todayStr ? 'in_progress' : 'consolidated') : 'unavailable');
        const valid = dayData && status !== 'pending' && status !== 'unavailable';

        weekly.push({
          date: dateStr,
          day_name: dayNames[date.getDay()],
          day_label: dayWithDate,
          people_count: valid ? dayData.people : null,
          detections_count: valid ? dayData.detections : null,
          status,
          prev_year_count: undefined,
          prev_year_date: '',
        });
      }

      // 📅 Carregar dados do mesmo período do ano anterior (histórico importado)
      // ⚠️ Só comparar com histórico quando MOSTRANDO TODAS as câmeras.
      //    Dados históricos são da org inteira — não faz sentido comparar
      //    com uma câmera específica (não temos histórico por câmera).
      if (!camId && !camIds?.length) {
        try {
        const prevYearStart = new Date(weekAgo);
        prevYearStart.setFullYear(prevYearStart.getFullYear() - 1);
        const prevYearEnd = new Date(today);
        prevYearEnd.setFullYear(prevYearEnd.getFullYear() - 1);
        const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const historical = await HistoricalImportService.getWeeklyHistorical(fmt(prevYearStart), fmt(prevYearEnd));
        if (historical.has_data) {
          const histByDate: Record<string, number> = {};
          historical.days.forEach(d => { histByDate[d.date] = d.people_count; });
          weekly.forEach((day, idx) => {
            const prevDate = new Date(weekAgo);
            prevDate.setDate(prevDate.getDate() + idx);
            prevDate.setFullYear(prevDate.getFullYear() - 1);
            const prevDateStr = fmt(prevDate);
            day.prev_year_count = histByDate[prevDateStr] ?? null;
            day.prev_year_date = prevDateStr;
          });
        }
      } catch (e) {
        // Não bloqueia se falhar — comparativo é opcional
      }
      } // fim if (!camId && !camIds?.length)

      setWeeklyData(weekly);

      // 📝 Carregar observações do período
      let observations: DayObservation[] = [];
      try {
        const weekAgoStr = `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, '0')}-${String(weekAgo.getDate()).padStart(2, '0')}`;
        observations = await DayObservationService.list(weekAgoStr, todayStr);
        setWeeklyObservations(observations);
      } catch (e) {
        // Não bloqueia o dashboard se falhar
        console.warn('Erro ao carregar observações:', e);
      }
      
      // 💾 Salvar no cache (inclui observações para restaurar no próximo reload)
      localStorage.setItem(weeklyCacheKey, JSON.stringify({
        data: weekly,
        observations,
        timestamp: new Date().toISOString(),
      }));
    } catch (err) {
      console.error('Erro ao carregar dados semanais:', err);
    } finally {
      setLoadingWeekly(false);
    }
  }, [effectiveOrgId, orgTimezone]);

  // 📊 Carregar dados mensais (últimos 12 meses) - COM CACHE + DADOS HISTÓRICOS
  const loadMonthlyData = useCallback(async () => {
    setLoadingMonthly(true);
    try {
      // 📷 Ler filtro de câmera do ref (sempre atualizado)
      const camId = cameraFilterRef.current.cameraId;
      const camFilterPart = camId || 'all';

      const today = new Date();
      const monthlyCacheKey = `monthly_combined_${today.getFullYear()}_${today.getMonth()}_${effectiveOrgId}_${camFilterPart}`;
      
      // 🚀 Tentar carregar do cache primeiro
      const cached = localStorage.getItem(monthlyCacheKey);
      if (cached) {
        try {
          const cachedData = JSON.parse(cached);
          const cacheTime = new Date(cachedData.timestamp);
          const now = new Date();
          const minutesDiff = (now.getTime() - cacheTime.getTime()) / (1000 * 60);

          const isCurrentMonth = cacheTime.getMonth() === now.getMonth() && cacheTime.getFullYear() === now.getFullYear();
          const cacheTTLMinutes = isCurrentMonth ? 30 : 360;

          if (minutesDiff < cacheTTLMinutes) {
            setMonthlyData(cachedData.data);
            setLoadingMonthly(false);
            return;
          }
        } catch (e) {
          console.warn('Cache mensal inválido, recarregando...');
        }
      }
      
      // 🔄 Usar endpoint combinado (AWFlux + dados históricos) — 24 meses para comparativo anual
      const response = await HistoricalImportService.getMonthlyCombined(24, camId || undefined);
      setMonthlyData(response.months);
      
      // 💾 Salvar no cache
      const monthlySaveCacheKey = `monthly_combined_${today.getFullYear()}_${today.getMonth()}_${effectiveOrgId}_${camFilterPart}`;
      localStorage.setItem(monthlySaveCacheKey, JSON.stringify({
        data: response.months,
        timestamp: new Date().toISOString(),
      }));
    } catch (err) {
      console.error('Erro ao carregar dados mensais:', err);
    } finally {
      setLoadingMonthly(false);
    }
  }, [effectiveOrgId]);

  // ⚡ Carregar dados semanais e mensais com delay (não bloquear UI inicial)
  useEffect(() => {
    // Delay de 500ms para permitir que dados principais carreguem primeiro
    const timer = setTimeout(() => {
      loadWeeklyData();
      loadMonthlyData();
    }, 500);

    // 🔄 Recarregar gráfico mensal a cada 30 minutos automaticamente
    const monthlyInterval = setInterval(() => {
      // Forçar invalidação do cache antes de recarregar
      const today = new Date();
      const monthlyCacheKey = `monthly_combined_${today.getFullYear()}_${today.getMonth()}_${effectiveOrgId}`;
      localStorage.removeItem(monthlyCacheKey);
      loadMonthlyData();
    }, 30 * 60 * 1000); // 30 minutos

    return () => {
      clearTimeout(timer);
      clearInterval(monthlyInterval);
    };
  }, [loadWeeklyData, loadMonthlyData, effectiveOrgId]);

  // 🔄 Recarregar dados semanais/mensais ao mudar filtro de câmera
  useEffect(() => {
    loadWeeklyData();
    loadMonthlyData();
  }, [currentFilters.cameraId, currentFilters.cameraIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔄 Preserve cache cleanup on group organization switches. Reloading is
  // handled by the keyed component, after the new auth context is committed.
  useEffect(() => {
    const handleOrgSwitch = () => {
      // Limpar caches antigos de todas as orgs
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('weekly_data_') || key.startsWith('monthly_combined_') || key.startsWith('prev_year_hist_')) {
          localStorage.removeItem(key);
        }
      });
    };
    window.addEventListener('auth:org-switch', handleOrgSwitch);
    return () => window.removeEventListener('auth:org-switch', handleOrgSwitch);
  }, []);

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (format === 'excel') setExportingExcel(true);
    else setExportingPdf(true);
    try {
      if (format === 'excel') {
        await ReportsService.exportExcel(currentFilters);
        showToast('Excel exportado com sucesso!', 'success');
      } else {
        await ReportsService.exportPDF(currentFilters);
        showToast('PDF exportado com sucesso!', 'success');
      }
    } catch (err) {
      console.error('Erro ao exportar:', err);
      showToast('Erro ao exportar relatório. Tente novamente.', 'error');
    } finally {
      if (format === 'excel') setExportingExcel(false);
      else setExportingPdf(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    if (isFiltered) {
      await handleFilterChange(currentFilters);
    } else {
      await refresh();
      setLastUpdate(new Date());
    }
  }, [isFiltered, currentFilters]);

  // Determinar qual conjunto de dados usar
  const displayStats = isFiltered && filteredStats ? {
    today_people: filteredStats.summary.total_people,
    total_people: filteredStats.summary.total_people,
    average_people_per_hour: filteredStats.summary.average_per_detection,
    active_cameras: filteredStats.by_camera.filter(c => c.is_entrance !== false).length,
    total_cameras: entranceCameras.length,
  } : systemStats;

  const displayDemographics = isFiltered && filteredStats ? {
    total_people: filteredStats.summary.total_people,
    gender: {
      male: filteredStats.demographics.gender.male,
      female: filteredStats.demographics.gender.female,
      male_percentage: filteredStats.demographics.gender.male_percentage,
      female_percentage: filteredStats.demographics.gender.female_percentage,
    },
    age_groups: {
      age_8_12: filteredStats.demographics.age_groups.age_8_12,
      age_15_20: filteredStats.demographics.age_groups.age_15_20,
      age_25_32: filteredStats.demographics.age_groups.age_25_32,
      age_adults: filteredStats.demographics.age_groups.age_adults,
      age_60_plus: filteredStats.demographics.age_groups.age_60_plus,
      age_8_12_percentage: filteredStats.demographics.age_groups.age_8_12_percentage,
      age_15_20_percentage: filteredStats.demographics.age_groups.age_15_20_percentage,
      age_25_32_percentage: filteredStats.demographics.age_groups.age_25_32_percentage,
      age_adults_percentage: filteredStats.demographics.age_groups.age_adults_percentage,
      age_60_plus_percentage: filteredStats.demographics.age_groups.age_60_plus_percentage,
    },
  } : demographics;

  const displayHourlyStats = isFiltered && filteredStats ? {
    date: new Date().toISOString().split('T')[0],
    camera_id: currentFilters.cameraId || null,
    peak_hour: filteredStats.hourly_flow.length > 0
      ? filteredStats.hourly_flow.reduce((max, h) =>
          h.people > max.people ? h : max
        , filteredStats.hourly_flow[0])?.hour ?? 0
      : 0,
    total_people: filteredStats.summary.total_people,
    hourly_data: filteredStats.hourly_flow.map(h => ({
      hour: h.hour,
      people: h.people,
      detections: h.detections,
      average_people: h.people / (h.detections || 1),
      gender: h.gender,
      age_groups: h.age_groups,
    })),
  } : hourlyStats;

  if (loading && !systemStats && !filteredStats) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // ⚠️ NÃO bloqueia UI por erro - mantém dados antigos visíveis
  // if (error) {
  //   return <ErrorAlert title="Erro ao carregar dados" message={error} />;
  // }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 lg:p-6 transition-colors">
      {/* Header - Responsivo */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              AWFlux - {organizationName}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-1">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Última atualização:{' '}
                {lastUpdate.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: orgTimezone,
                })}
              </p>
              {/* Indicador WebSocket */}
              <div className="flex items-center gap-2 text-xs">
                <div 
                  className={`w-2 h-2 rounded-full ${
                    isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}
                  title={isConnected ? 'WebSocket conectado' : 'WebSocket desconectado'}
                />
                <span className="text-gray-500 dark:text-gray-400">
                  {isConnected ? 'Tempo real ativo' : 'Modo manual'}
                </span>
              </div>
              
              {/* Indicador de Filtragem */}
              {filterLoading && (
                <div className="flex items-center gap-2 text-xs bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
                  <span className="text-yellow-700 dark:text-yellow-400 font-medium">
                    Filtrando dados...
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading || filterLoading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || filterLoading) ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Painel de Filtros */}
      <FilterPanel
        onFilterChange={handleFilterChange}
        cameras={entranceCameras}
        onExport={handleExport}
        filterLoading={filterLoading}
        exportingExcel={exportingExcel}
        exportingPdf={exportingPdf}
      />

      {/* KPIs Principais - Grid Responsivo 2x2 no mobile */}
      {displayStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
          <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
            <div className="flex flex-col gap-1 mb-2">
              <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">Total de Pessoas {isFiltered ? '(Filtrado)' : '(Hoje)'}</p>
              <span className="text-[9px] sm:text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 sm:px-2 py-0.5 rounded-full w-fit">
                🚪 Entradas
              </span>
            </div>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400 my-auto">
              {displayStats.today_people}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1 sm:mt-2">
              {isFiltered ? 'período selecionado' : `${systemStats?.today_detections || 0} detecções`}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
            <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-2">Média por {isFiltered ? 'Detecção' : 'Hora'}</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600 dark:text-green-400 my-auto">
              {displayStats.average_people_per_hour.toFixed(1)}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1 sm:mt-2">pessoas/{isFiltered ? 'detecção' : 'hora'}</p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
            <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-2">Câmeras {isFiltered ? 'com Dados' : 'Ativas'}</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400 my-auto">
              {displayStats.active_cameras}/{displayStats.total_cameras}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1 sm:mt-2">em operação</p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 md:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex flex-col h-full">
            <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-2">Total {isFiltered ? 'no Período' : 'Geral'}</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600 dark:text-orange-400 my-auto">
              {displayStats.total_people}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-500 mt-1 sm:mt-2">
              {isFiltered ? 'dados filtrados' : `${systemStats?.total_detections || 0} detecções total`}
            </p>
          </div>
        </div>
      )}

      {/* � Pessoas no Estabelecimento (câmeras de saída) */}
      <PeopleInsidePanel
        systemStats={systemStats}
        filteredStats={filteredStats}
        isFiltered={isFiltered}
      />

      <ConversionPanel
        systemStats={systemStats}
        filteredStats={filteredStats}
        isFiltered={isFiltered}
        period={isFiltered && filteredStats ? filteredStats.period.type : 'today'}
        isUpdating={filterLoading}
        hasCameraFilter={!!currentFilters.cameraId || !!currentFilters.cameraIds?.length}
      />

      {/* Gráficos de Demografia - Grid Responsivo */}
      {(displayDemographics || demographics) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <GenderBreakdownChart
            data={(displayDemographics || demographics)!.gender}
            previousData={comparativeGender ?? undefined}
          />
          <AgeDistributionChart data={(displayDemographics || demographics)!.age_groups} />
        </div>
      )}

      {/* 🎭 Análise de Emoções - Feature Premium */}
      {/* ⚠️ SÓ aparece quando a organização tem emotion_analytics_enabled=TRUE. */}
      {/* Sem a flag, emotions.enabled é false → bloco nem renderiza e o */}
      {/* dashboard continua EXATAMENTE como antes da feature. */}
      {emotions && emotions.enabled && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <EmotionBreakdownChart data={emotions} />
            <EmotionDailySummaryCard data={emotions} />
          </div>
          <div className="mb-6">
            <EmotionByHourChart data={emotions} />
          </div>
        </>
      )}

      {/* Gráfico de Timeline */}
      {(displayHourlyStats || hourlyStats) && (
        <div className="mb-6">
          <TimelineChart data={displayHourlyStats || hourlyStats!} />
        </div>
      )}

      {/* Gráfico de Comparativo Mensal */}
      <div className="mb-6">
        {loadingMonthly ? (
          <div className="h-96 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <MonthlyComparisonChart data={monthlyData} />
        )}
      </div>

      {/* Gráfico de Fluxo Semanal (Últimos 7 Dias) */}
      <div className="mb-6">
        {loadingWeekly ? (
          <div className="h-96 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <WeeklyFlowChart data={weeklyData} observations={weeklyObservations} />
        )}
      </div>

      {/* Gráfico de Distribuição por Entradas */}
      <div className="mb-6">
        <EntranceExitDistribution 
          byCamera={filteredStats?.by_camera?.filter(c => c.is_entrance !== false) || []}
          cameras={entranceCameras}
        />
      </div>

      {/* Gráfico de Comparativo Temporal + Tabela Comparativa */}
      <div className="mb-6">
        {/* Toggle de visualização */}
        <div className="flex items-center justify-end mb-2">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setTemporalView('chart')}
              className={`px-4 py-1.5 text-sm font-medium transition ${
                temporalView === 'chart'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-pressed={temporalView === 'chart'}
            >
              Gráfico
            </button>
            <button
              type="button"
              onClick={() => setTemporalView('table')}
              className={`px-4 py-1.5 text-sm font-medium transition border-l border-gray-300 dark:border-gray-600 ${
                temporalView === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-pressed={temporalView === 'table'}
            >
              Tabela Comparativa
            </button>
          </div>
        </div>

        {temporalView === 'chart' ? (
          <TemporalComparisonChart
            detections={filteredStats?.daily_flow?.map((d) => ({
              id: `daily-${d.date}`,
              created_at: `${d.date}T12:00:00`,
              num_persons: d.people,
            })) || []}
            periodStart={filteredStats?.period.start}
            periodEnd={filteredStats?.period.end}
            prevYearData={filteredPrevYear ?? undefined}
          />
        ) : (
          <ComparativeTable
            current={comparativeDailyCurrent}
            previous={comparativeDailyPrevious}
            comparisonLabel={comparativeLabel || 'Período anterior'}
            isYearOverYear={comparativeIsYoY}
          />
        )}
      </div>

      {/* Lista de Câmeras com Estatísticas */}
      {systemStats && systemStats.camera_stats.filter(c => c.is_entrance !== false || c.is_conversion).length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white">
              Estatísticas por Câmera
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Totais acumulados desde o início da operação de cada câmera
            </p>
            {systemStats.camera_stats.some(c => c.is_conversion) && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                🛒 Câmeras de checkout são exibidas separadamente — contam pessoas no caixa para a taxa de conversão
              </p>
            )}
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                        #
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Câmera
                      </th>
                      <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cooldown
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Pessoas
                      </th>
                      <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        % Fluxo
                      </th>
                      <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Detecções
                      </th>
                      <th className="hidden xl:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {(() => {
                      // Câmeras de entrada + câmeras de conversão (checkout)
                      const allRelevantCams = systemStats.camera_stats
                        .filter(c => c.is_entrance !== false || c.is_conversion)
                        .sort((a, b) => {
                          // Câmeras de checkout sempre ao final
                          if (a.is_conversion && !b.is_conversion) return 1;
                          if (!a.is_conversion && b.is_conversion) return -1;
                          return b.total_people - a.total_people;
                        });
                      const entranceCams = allRelevantCams.filter(c => !c.is_conversion);
                      const totalEntrancePeople = entranceCams.reduce((s, c) => s + c.total_people, 0);
                      return allRelevantCams.map((camera, idx) => {
                        const isConvCam = !!camera.is_conversion;
                        const pct = !isConvCam && totalEntrancePeople > 0
                          ? (camera.total_people / totalEntrancePeople) * 100
                          : null;
                        const convRate = isConvCam && totalEntrancePeople > 0
                          ? (camera.total_people / totalEntrancePeople * 100)
                          : null;
                        const status = getCameraStatus(camera.last_detection ?? undefined);
                        const rowBg = isConvCam
                          ? 'bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700';
                        return (
                          <tr key={camera.camera_id} className={rowBg}>
                            {/* Ranking */}
                            <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-center whitespace-nowrap">
                              {!isConvCam ? (
                                <span className="text-xs font-bold text-gray-400 dark:text-gray-500">
                                  {idx + 1}º
                                </span>
                              ) : (
                                <span className="text-xs font-bold text-amber-500">🛒</span>
                              )}
                            </td>
                            {/* Câmera: nome + badge de checkout em mobile */}
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1.5">
                                {camera.camera_name}
                                {isConvCam && (
                                  <span className="sm:hidden px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                    🛒
                                  </span>
                                )}
                              </div>
                            </td>
                            {/* Tipo */}
                            <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              {isConvCam ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                  🛒 Checkout
                                </span>
                              ) : camera.is_entrance ? (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  🚪 Entrada
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  📊 Interna
                                </span>
                              )}
                            </td>
                            {/* Cooldown */}
                            <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              {camera.cooldown_seconds ? (
                                <div className="text-sm">
                                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                                    ⏱️ {camera.cooldown_seconds}s
                                  </span>
                                  {camera.cooldown_seconds >= 60 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      ({Math.floor(camera.cooldown_seconds / 60)} min)
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ⚙️ Padrão (3 min)
                                </span>
                              )}
                            </td>
                            {/* Pessoas */}
                            <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                {camera.total_people.toLocaleString('pt-BR')}
                              </div>
                            </td>
                            {/* % Fluxo ou Taxa de Conversão */}
                            <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              {isConvCam ? (
                                convRate !== null ? (
                                  <span className="text-xs font-semibold text-amber-600 dark:text-amber-400" title="Taxa de conversão: checkout / entradas">
                                    {convRate.toFixed(1)}% conv.
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">—</span>
                                )
                              ) : pct !== null ? (
                                <div className="flex items-center gap-2 min-w-[80px]">
                                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-400 dark:bg-blue-500 rounded-full"
                                      style={{ width: `${pct.toFixed(1)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums shrink-0">
                                    {pct.toFixed(1)}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            {/* Detecções */}
                            <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-300">
                                {camera.total_detections.toLocaleString('pt-BR')}
                              </div>
                            </td>
                            {/* Status — dot colorido + última detecção */}
                            <td className="hidden xl:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-2 h-2 rounded-full shrink-0 ${status.dot}`}
                                  title={status.label}
                                />
                                <div>
                                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    {status.label}
                                  </div>
                                  <div className="text-xs text-gray-400 dark:text-gray-500">
                                    {camera.last_detection
                                      ? new Date(camera.last_detection).toLocaleString('pt-BR', { timeZone: orgTimezone })
                                      : 'Nunca'}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};
